/**
 * №53 (53.0, D-CR14) — THE RAIL FOOT IS ONE PROFILE ROW.
 *
 * Avatar · name · chevron, and a menu holding everything that is not the day:
 * who you are, which workspaces you can be in, and the way into Settings.
 *
 * ── What this replaces, and why ──────────────────────────────────────────────
 *
 * The foot used to be three stacked blocks: `WorkspaceSwitcher`, then a row of
 * ghost buttons (theme toggle, Console link), then an avatar. David rejected the
 * first cut of this rung on sight — *"It looks hideous — yuk"* — and the reason is
 * structural rather than cosmetic: that is three blocks and two icon shapes
 * crammed into the end of a 184px column, each a different height, none of them
 * saying what it is for. `docs/53-mockups/rail-foot.html` is the rejected cut and
 * is kept, marked, as the record of it.
 *
 * The replacement is the shape every desktop application already uses, so nobody
 * has to learn it: one row that is a person, and a menu behind it.
 *
 * ── The workspaces are LISTED, not nested ────────────────────────────────────
 *
 * D-CR14, from the approved mockup: the workspaces appear as rows in this menu,
 * not behind a "Switch workspace…" submenu. Most people have two or three, and a
 * submenu to reach a three-item list is a click that buys nothing. If a firm ever
 * carries more than `WORKSPACE_LIST_MAX`, the list collapses to a single row that
 * opens ⌘K filtered to workspaces — the search is already there, and a scrolling
 * menu inside a 184px rail is worse than either.
 *
 * ── `WorkspaceSwitcher` is re-skinned, not rebuilt ───────────────────────────
 *
 * D-CR14 says so explicitly. The switching BEHAVIOUR — `switchWorkspace`, and the
 * session-scoped toast warning that connected MCP clients do not follow you — is
 * non-obvious, was got right once, and is imported from there rather than
 * re-implemented here. What this file owns is the presentation.
 *
 * ── The `Console` link is DELETED, not restyled (D-CR21) ─────────────────────
 *
 * There is no row for it here and there is no row for it in the rail. `/app` is
 * the only product surface; the maker surface's jobs become Settings pages, which
 * is what the `Settings` row below opens. Nothing in this menu says "console".
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useV3Theme } from "../../hooks/use-v3-theme";
import { switchWorkspaceWithHint } from "../../components/shell/switch-workspace";

/**
 * Above this many workspaces the list becomes one row that opens ⌘K filtered to
 * workspaces. Six is the mockup's stated threshold ("more than about six").
 */
const WORKSPACE_LIST_MAX = 6;

/** Initials for the avatar — two characters, never more, never a broken glyph. */
function initials(nameOrEmail: string): string {
  const parts = nameOrEmail.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return nameOrEmail.slice(0, 2).toUpperCase();
}

export function ShellProfileMenu({ onOpenPalette }: { onOpenPalette?: (seed: string) => void }) {
  const { user, currentWorkspaceId, logout } = useAuth();
  const { theme, toggle } = useV3Theme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Dismiss on outside click AND on Escape. Escape matters more than it looks:
  // this menu sits at the bottom of the rail, so a pointer-only dismissal leaves
  // a keyboard user with a trapped popover over the one control they were using.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const workspaces = user?.workspaces ?? [];
  const displayName = user?.name?.trim() || user?.email || "Signed in";
  const listWorkspaces = workspaces.length <= WORKSPACE_LIST_MAX;

  async function go(id: string) {
    setOpen(false);
    if (id === currentWorkspaceId) return;
    await switchWorkspaceWithHint(id);
    // Navigation stays with the caller (see the helper's note): the App Shell
    // lands on its own home, never on the maker surface.
    navigate("/app");
  }

  return (
    <div className="profilemenu" ref={ref}>
      {open && (
        <div className="profilemenu__pop" role="menu" aria-label="Account and workspace">
          <div className="profilemenu__who">
            <div className="profilemenu__whoname">{displayName}</div>
            {user?.email && <div className="profilemenu__whomail">{user.email}</div>}
          </div>

          <div className="profilemenu__group">Workspace</div>
          {listWorkspaces ? (
            <>
              {workspaces.map((w) => (
                <button
                  key={w.id}
                  role="menuitem"
                  className={`profilemenu__ws${w.id === currentWorkspaceId ? " is-current" : ""}`}
                  onClick={() => void go(w.id)}
                >
                  {/* A square per workspace, so the list has a shape to scan before
                      it is read. The mockup draws each one a DIFFERENT colour; these
                      are all the shell accent, because a workspace has no colour of
                      its own in the data — `data-shell-theme` carries a hue only when
                      a workspace has chosen one, and inventing a per-id hue here
                      would be decoration dressed as identity. Recorded as a fidelity
                      gap rather than faked; it resolves when the switcher can read
                      each workspace's own theme. */}
                  <span className="profilemenu__wsmark" aria-hidden="true" />
                  <span className="profilemenu__wsname">{w.name}</span>
                  <span className="profilemenu__wsrole">{w.role}</span>
                </button>
              ))}
              <button role="menuitem" className="profilemenu__ws profilemenu__ws--new" onClick={() => { setOpen(false); navigate("/app/settings/workspace"); }}>
                <span className="profilemenu__wsmark profilemenu__wsmark--new" aria-hidden="true" />
                <span className="profilemenu__wsname">New workspace</span>
              </button>
            </>
          ) : (
            // Past the threshold the list is a search, not a menu (D-CR14).
            <button
              role="menuitem"
              className="profilemenu__row"
              onClick={() => { setOpen(false); onOpenPalette?.(""); }}
            >
              <span>Switch workspace</span>
              <span className="profilemenu__hint">{workspaces.length} · ⌘K</span>
            </button>
          )}

          <div className="profilemenu__rule" />

          <button role="menuitem" className="profilemenu__row" onClick={() => { setOpen(false); navigate("/app/settings"); }}>
            <span>Settings</span>
            <span className="profilemenu__hint">firm · workspace</span>
          </button>
          <button role="menuitem" className="profilemenu__row" onClick={toggle}>
            <span>Appearance</span>
            <span className="profilemenu__hint">{theme === "dark" ? "dark" : "light"}</span>
          </button>
          <button role="menuitem" className="profilemenu__row" onClick={() => { setOpen(false); onOpenPalette?.(""); }}>
            <span>Keyboard shortcuts</span>
            <span className="profilemenu__hint">⌘K</span>
          </button>

          <div className="profilemenu__rule" />

          <button role="menuitem" className="profilemenu__row" onClick={() => { setOpen(false); void logout(); }}>
            <span>Sign out</span>
          </button>
        </div>
      )}

      <button
        className={`profilerow${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="shell-profile-row"
      >
        <span className="profilerow__av" aria-hidden="true">{initials(user?.name || user?.email || "?")}</span>
        <span className="profilerow__name">{displayName}</span>
        <svg className="profilerow__chev" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.4} aria-hidden="true">
          <path d="M4.5 6.5L8 10l3.5-3.5" />
        </svg>
      </button>
    </div>
  );
}
